import { Alert, Snackbar, Box, LinearProgress, Typography } from '@mui/material';
import { CloudOff, CloudQueue, CloudDone } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useAppSelector } from '../../store/hooks';
import { selectIsOffline, selectSyncInProgress, selectSyncProgress, selectLastSyncTimestamp, selectPendingActionsCount } from '../../store/slices/uiSlice';

interface OfflineBannerProps {
  /**
   * Whether the app is currently offline (overrides Redux state if provided)
   */
  isOffline?: boolean;
  /**
   * Sync status information (overrides Redux state if provided)
   */
  syncStatus?: {
    isSyncing: boolean;
    progress?: number;
    lastSyncTime?: Date;
    pendingActions?: number;
  };
  /**
   * Whether to show cached data notice
   */
  showCachedNotice?: boolean;
  /**
   * Custom message to display
   */
  customMessage?: string;
}

/**
 * OfflineBanner Component
 * 
 * Displays offline status, sync progress, and cached data notices with theme support.
 * Automatically detects online/offline status and shows appropriate messaging.
 * Integrates with Redux store for centralized state management.
 * 
 * Features:
 * - Automatic offline detection using navigator.onLine and Redux state
 * - Sync progress indicator with percentage
 * - Cached data notice for offline mode
 * - Pending actions counter
 * - Last sync timestamp display
 * - Auto-hide when online and no sync in progress
 * - Full dark mode support using CSS variables
 * - Theme-aware colors for all states
 */
export default function OfflineBanner({
  isOffline: isOfflineProp,
  syncStatus: syncStatusProp,
  showCachedNotice = false,
  customMessage,
}: OfflineBannerProps) {
  // Get state from Redux store
  const reduxIsOffline = useAppSelector(selectIsOffline);
  const reduxSyncInProgress = useAppSelector(selectSyncInProgress);
  const reduxSyncProgress = useAppSelector(selectSyncProgress);
  const reduxLastSyncTimestamp = useAppSelector(selectLastSyncTimestamp);
  const reduxPendingActionsCount = useAppSelector(selectPendingActionsCount);

  // Use prop values if provided, otherwise use Redux state
  const isOffline = isOfflineProp !== undefined ? isOfflineProp : reduxIsOffline;
  const isSyncing = syncStatusProp?.isSyncing !== undefined ? syncStatusProp.isSyncing : reduxSyncInProgress;
  const syncProgress = syncStatusProp?.progress !== undefined ? syncStatusProp.progress : reduxSyncProgress;
  const lastSyncTimestamp = syncStatusProp?.lastSyncTime ? syncStatusProp.lastSyncTime.getTime() : reduxLastSyncTimestamp;
  const pendingActionsCount = syncStatusProp?.pendingActions !== undefined ? syncStatusProp.pendingActions : reduxPendingActionsCount;

  const [isOfflineState, setIsOfflineState] = useState<boolean>(isOffline);
  const [open, setOpen] = useState<boolean>(false);

  // Listen to online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOfflineState(false);
    };

    const handleOffline = () => {
      setIsOfflineState(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Update offline state when prop or Redux state changes
  useEffect(() => {
    setIsOfflineState(isOffline);
  }, [isOffline]);

  // Determine if banner should be shown
  useEffect(() => {
    const shouldShow =
      isOfflineState ||
      isSyncing ||
      (showCachedNotice && isOfflineState) ||
      (pendingActionsCount > 0) ||
      !!customMessage;

    setOpen(shouldShow);
  }, [isOfflineState, isSyncing, showCachedNotice, customMessage, pendingActionsCount]);

  // Determine banner severity and icon
  const getSeverityAndIcon = () => {
    if (customMessage) {
      return { severity: 'info' as const, icon: <CloudQueue /> };
    }
    if (isSyncing) {
      return { severity: 'info' as const, icon: <CloudQueue /> };
    }
    if (isOfflineState) {
      return { severity: 'warning' as const, icon: <CloudOff /> };
    }
    if (pendingActionsCount > 0) {
      return { severity: 'info' as const, icon: <CloudQueue /> };
    }
    return { severity: 'success' as const, icon: <CloudDone /> };
  };

  // Generate banner message
  const getMessage = () => {
    if (customMessage) {
      return customMessage;
    }

    if (isSyncing) {
      return `Syncing data${syncProgress !== undefined && syncProgress > 0 ? ` (${Math.round(syncProgress)}%)` : ''}...`;
    }

    if (isOfflineState) {
      const parts: string[] = ['You are currently offline'];
      
      if (showCachedNotice) {
        parts.push('Viewing cached data');
      }
      
      if (pendingActionsCount > 0) {
        parts.push(`${pendingActionsCount} action${pendingActionsCount > 1 ? 's' : ''} queued for sync`);
      }
      
      return parts.join('. ');
    }

    if (pendingActionsCount > 0) {
      return `${pendingActionsCount} action${pendingActionsCount > 1 ? 's' : ''} pending sync`;
    }

    if (lastSyncTimestamp) {
      const now = new Date();
      const diff = now.getTime() - lastSyncTimestamp;
      const minutes = Math.floor(diff / 60000);
      
      if (minutes < 1) {
        return 'Synced just now';
      } else if (minutes < 60) {
        return `Last synced ${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      } else {
        const hours = Math.floor(minutes / 60);
        return `Last synced ${hours} hour${hours > 1 ? 's' : ''} ago`;
      }
    }

    return 'Connected';
  };

  const { severity, icon } = getSeverityAndIcon();
  const message = getMessage();

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{
        top: { xs: 8, sm: 16 },
        '& .MuiSnackbar-root': {
          position: 'static',
        },
      }}
    >
      <Alert
        severity={severity}
        icon={icon}
        sx={{
          width: '100%',
          minWidth: { xs: '90vw', sm: 400 },
          maxWidth: { xs: '95vw', sm: 600 },
          boxShadow: 3,
          backgroundColor: severity === 'warning' 
            ? 'var(--color-warning-background, #fff3e0)' 
            : severity === 'info'
            ? 'var(--color-info-background, #e3f2fd)'
            : severity === 'success'
            ? 'var(--color-success-background, #e8f5e9)'
            : 'var(--color-surface, #ffffff)',
          color: severity === 'warning'
            ? 'var(--color-warning-text, #e65100)'
            : severity === 'info'
            ? 'var(--color-info-text, #01579b)'
            : severity === 'success'
            ? 'var(--color-success-text, #2e7d32)'
            : 'var(--color-text-primary, rgba(0, 0, 0, 0.87))',
          '& .MuiAlert-icon': {
            color: 'inherit',
          },
        }}
      >
        <Box>
          <Typography 
            variant="body2" 
            component="div"
            sx={{
              color: 'inherit',
            }}
          >
            {message}
          </Typography>
          {isSyncing && syncProgress !== undefined && syncProgress > 0 && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={syncProgress}
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: 'var(--color-border, rgba(0, 0, 0, 0.12))',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'var(--color-primary, #0d47a1)',
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Alert>
    </Snackbar>
  );
}